import React, { useState, useEffect } from 'react';
import { X, Plus, Edit2, Trash2, Check, Tag } from 'lucide-react';
import Swal from 'sweetalert2';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../../services/newsNewsletterApi';

const TUNISIAN_ENERGY_DOMAINS = [
  { key: 'SOLAR_PV', label: 'Solaire Photovoltaïque' },
  { key: 'SOLAR_THERMAL', label: 'Solaire Thermique' },
  { key: 'WIND', label: 'Éolien' },
  { key: 'BIOMASS', label: 'Biomasse' },
  { key: 'ENERGY_EFFICIENCY', label: 'Efficacité Énergétique' },
  { key: 'REGULATION_STEG', label: 'Réglementation STEG' },
  { key: 'FISCAL_INCENTIVES', label: 'Incitations Fiscales' },
  { key: 'CARBON_FOOTPRINT', label: 'Bilan Carbone' },
  { key: 'HYDROGEN_GREEN', label: 'Hydrogène Vert' },
  { key: 'GENERAL_ENERGY', label: 'Énergie Générale' },
];

export default function CategoryManagerModal({ isOpen, onClose, onCategoriesUpdated }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#0F2343',
    defaultTunisianDomains: ['SOLAR_PV'],
    displayOrder: 0,
    isActive: true,
  });

  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const res = await getCategories({ includeInactive: true });
      if (res?.success) {
        setCategories(res.data || []);
      }
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      name: '',
      description: '',
      color: '#0F2343',
      defaultTunisianDomains: ['SOLAR_PV'],
      displayOrder: 0,
      isActive: true,
    });
  };

  const handleEdit = (cat) => {
    setEditingId(cat._id);
    setFormData({
      name: cat.name || '',
      description: cat.description || '',
      color: cat.color || '#0F2343',
      defaultTunisianDomains: cat.defaultTunisianDomains || ['SOLAR_PV'],
      displayOrder: cat.displayOrder || 0,
      isActive: cat.isActive !== false,
    });
  };

  const toggleDomain = (domainKey) => {
    setFormData((prev) => {
      const exists = prev.defaultTunisianDomains.includes(domainKey);
      const next = exists
        ? prev.defaultTunisianDomains.filter((d) => d !== domainKey)
        : [...prev.defaultTunisianDomains, domainKey];
      return { ...prev, defaultTunisianDomains: next.length > 0 ? next : ['SOLAR_PV'] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      Swal.fire('Validation', 'Le nom de la catégorie est obligatoire.', 'warning');
      return;
    }

    try {
      if (editingId) {
        await updateCategory(editingId, formData);
        Swal.fire('Succès', 'Catégorie mise à jour avec succès.', 'success');
      } else {
        await createCategory(formData);
        Swal.fire('Succès', 'Nouvelle catégorie créée avec succès.', 'success');
      }
      resetForm();
      await loadCategories();
      onCategoriesUpdated?.();
    } catch (err) {
      Swal.fire('Erreur', err.message || 'Impossible de sauvegarder la catégorie', 'error');
    }
  };

  const handleDelete = async (cat) => {
    const confirm = await Swal.fire({
      title: 'Supprimer cette catégorie ?',
      text: `Êtes-vous sûr de vouloir supprimer "${cat.name}" ?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#E53E3E',
    });

    if (confirm.isConfirmed) {
      try {
        await deleteCategory(cat._id);
        Swal.fire('Supprimée', 'Catégorie supprimée.', 'success');
        await loadCategories();
        onCategoriesUpdated?.();
      } catch (err) {
        Swal.fire('Erreur', err.message, 'error');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="an-modal-backdrop animate-fade-in" onClick={onClose}>
      <div className="an-modal-card an-modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="an-modal-header">
          <div className="an-modal-title">
            <Tag size={22} className="text-primary mr-2" />
            <span>Gestion des Catégories Énergétiques</span>
          </div>
          <button className="an-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="an-modal-body">
          <div className="an-cat-manager-grid">
            {/* Left: Categories List */}
            <div className="an-cat-list-panel">
              <h4 className="an-sub-title">Catégories existantes ({categories.length})</h4>
              {loading ? (
                <div className="an-loading-state">Chargement...</div>
              ) : categories.length === 0 ? (
                <div className="an-empty-state">Aucune catégorie trouvée.</div>
              ) : (
                <div className="an-cat-list">
                  {categories.map((cat) => (
                    <div
                      key={cat._id}
                      className={`an-cat-item ${editingId === cat._id ? 'selected' : ''}`}
                    >
                      <div className="an-cat-item-info">
                        <span
                          className="an-cat-color-dot"
                          style={{ backgroundColor: cat.color || '#0F2343' }}
                        />
                        <span className="an-cat-item-name">{cat.name}</span>
                        {!cat.isActive && <span className="an-badge-inactive">Inactif</span>}
                      </div>
                      <div className="an-cat-item-actions">
                        <button
                          type="button"
                          className="an-btn-icon edit"
                          onClick={() => handleEdit(cat)}
                          title="Modifier"
                        >
                          <Edit2 size={15} />
                        </button>
                        <button
                          type="button"
                          className="an-btn-icon delete"
                          onClick={() => handleDelete(cat)}
                          title="Supprimer"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Form */}
            <div className="an-cat-form-panel">
              <h4 className="an-sub-title">
                {editingId ? 'Modifier la catégorie' : 'Ajouter une catégorie'}
              </h4>
              <form onSubmit={handleSubmit} className="an-cat-form">
                <div className="an-form-group">
                  <label>Nom de la catégorie *</label>
                  <input
                    type="text"
                    className="an-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Énergie Solaire & PV"
                    required
                  />
                </div>

                <div className="an-form-group">
                  <label>Description éditoriale</label>
                  <textarea
                    className="an-textarea"
                    rows={2}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brève description de la thématique..."
                  />
                </div>

                <div className="an-form-row">
                  <div className="an-form-group flex-1">
                    <label>Couleur d'accentuation</label>
                    <div className="an-color-picker-wrapper">
                      <input
                        type="color"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="an-color-input"
                      />
                      <input
                        type="text"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="an-input text-uppercase"
                        maxLength={7}
                      />
                    </div>
                  </div>

                  <div className="an-form-group flex-1">
                    <label>Ordre d'affichage</label>
                    <input
                      type="number"
                      className="an-input"
                      value={formData.displayOrder}
                      onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value, 10) || 0 })}
                    />
                  </div>
                </div>

                <div className="an-form-group">
                  <label>Domaines énergétiques tunisiens associés</label>
                  <div className="an-domain-chips">
                    {TUNISIAN_ENERGY_DOMAINS.map((domain) => {
                      const isSelected = formData.defaultTunisianDomains.includes(domain.key);
                      return (
                        <button
                          key={domain.key}
                          type="button"
                          className={`an-domain-chip ${isSelected ? 'active' : ''}`}
                          onClick={() => toggleDomain(domain.key)}
                        >
                          {isSelected && <Check size={13} className="mr-1" />}
                          {domain.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="an-form-actions">
                  {editingId && (
                    <button
                      type="button"
                      className="an-btn-secondary"
                      onClick={resetForm}
                    >
                      Annuler
                    </button>
                  )}
                  <button type="submit" className="an-btn-primary">
                    <Plus size={16} />
                    <span>{editingId ? 'Mettre à jour' : 'Créer la catégorie'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

